import { createSlice } from '@reduxjs/toolkit';

import type { TProduct } from 'api/models/TProduct';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

export type QuoteState = {
  isLoading: boolean;
  errors: NormalizedError[] | null;
  data: {
    code: string | null;
    products: TProduct[];
  };
  currentProduct: TProduct | null;
  isCompareView: boolean;
  tariffTypeFilter: string[] | null;
  isDualRate: boolean;
};

export const initialState: QuoteState = {
  isLoading: false,
  errors: null,
  data: {
    code: null,
    products: []
  },
  currentProduct: null,
  isCompareView: false,
  tariffTypeFilter: null,
  isDualRate: false
};

export const slice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload;
      state.errors = initialState.errors;
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.data = initialState.data;
      state.errors = payload;
    },
    setCurrentProduct: (state, { payload }) => {
      state.currentProduct = payload;
    },
    setIsCompareView: (state, { payload }) => {
      state.isCompareView = payload;
    },
    setTariffTypeFilter: (state, { payload }) => {
      state.tariffTypeFilter = payload;
    },
    setIsDualRate: (state, { payload }) => {
      state.isDualRate = payload;
    },
    setPriorityProductByCode: (state, { payload: productCode }) => {
      const products = state.data.products;
      const priorityProductIndex = (products as TProduct[]).findIndex(
        (product) => product.code === productCode
      );

      const [priorityProduct] = products.splice(priorityProductIndex, 1);
      state.data.products.unshift(priorityProduct);
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setSuccess,
  setCurrentProduct,
  setError,
  setIsCompareView,
  setTariffTypeFilter,
  setIsDualRate,
  setPriorityProductByCode
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectData = (state: RootState) => selectSlice(state).data;
export const selectProducts = (state: RootState) => selectData(state).products;
export const selectTariffTypeFilter = (state: RootState) =>
  selectSlice(state).tariffTypeFilter;
export const selectFilteredProducts = (state: RootState) => {
  const allProducts = selectProducts(state);
  const tariffTypeFilter = selectTariffTypeFilter(state);

  if (!tariffTypeFilter) {
    return allProducts;
  }

  return allProducts.filter((product) => {
    const til = product.electricityTil || product.gasTil;
    const tariffType = til?.tariffType;
    return tariffType ? tariffTypeFilter.includes(tariffType) : false;
  });
};
export const selectTariffTypes = (state: RootState) =>
  selectProducts(state).reduce((acc, product) => {
    const til = product.electricityTil || product.gasTil;
    const tariffType = til?.tariffType;
    if (tariffType && !acc.includes(tariffType)) {
      return [...acc, tariffType];
    }
    return acc;
  }, [] as string[]);
export const selectIsDualRate = (state: RootState) =>
  selectSlice(state).isDualRate;

export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;
export const selectErrors = (state: RootState) => selectSlice(state).errors;

export const selectProductByCode = (productCode?: string) => (
  state: RootState
) => {
  const products = selectProducts(state);

  return products?.find((product) => product.code === productCode);
};
export const selectIsCompareView = (state: RootState) =>
  selectSlice(state).isCompareView;

// currentProduct
export const selectCurrentProduct = (state: RootState): TProduct =>
  (selectSlice(state).currentProduct || {}) as TProduct;
export const selectProductName = (state: RootState) =>
  selectCurrentProduct(state)?.name;
export const selectGasQuote = (state: RootState) =>
  selectCurrentProduct(state)?.gasTil;
export const selectGasTariffCode = (state: RootState) => {
  const gasQuote = selectGasQuote(state);

  return gasQuote?.tariffCode;
};
export const selectElectricityQuote = (state: RootState) =>
  selectCurrentProduct(state)?.electricityTil;

export const selectElectricityTariffCode = (state: RootState) => {
  const electricityQuote = selectElectricityQuote(state);

  return electricityQuote?.tariffCode;
};

export const selectTariffEndsOn = (state: RootState) => {
  const electricityQuote = selectElectricityQuote(state);
  const gasQuote = selectGasQuote(state);

  return electricityQuote?.tariffEndsOn || gasQuote?.tariffEndsOn;
};

export const selectPriceGuranteedUntil = (state: RootState) => {
  const electricityQuote = selectElectricityQuote(state);
  const gasQuote = selectGasQuote(state);

  return (
    electricityQuote?.priceGuaranteedUntil || gasQuote?.priceGuaranteedUntil
  );
};

export const selectProductFeatures = (state: RootState) =>
  selectCurrentProduct(state)?.features;

export const selectSpecifics = (state: RootState) =>
  selectCurrentProduct(state)?.specifics;

export const selectTariffType = (state: RootState) => {
  const electricityQuote = selectElectricityQuote(state);
  const gasQuote = selectGasQuote(state);

  return electricityQuote?.tariffType || gasQuote?.tariffType;
};
export const selectTOUDefinition = (state: RootState) => {
  return selectCurrentProduct(state)?.touDefinition;
};
export const selectProductAnnualAmount = (state: RootState) =>
  selectCurrentProduct(state)?.annualAmount;
export const selectMonthlyAmount = (state: RootState) =>
  selectCurrentProduct(state)?.monthlyAmount;

export default slice.reducer;
